"use client"
import { useState, useEffect, useRef } from "react"
import { useLanguage } from "../contexts/LanguageContext"

const Header = () => {
  const { language, setLanguage, t } = useLanguage()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMobileLanguageDropdownOpen, setIsMobileLanguageDropdownOpen] = useState(false)
  const [isDesktopLanguageDropdownOpen, setIsDesktopLanguageDropdownOpen] = useState(false)
  const navRef = useRef(null)
  const desktopMenuToggleRef = useRef(null)
  const mobileMenuToggleRef = useRef(null)
  const mobileLanguageDropdownRef = useRef(null)
  const desktopLanguageDropdownRef = useRef(null)

  const languages = [
    { code: "en", label: "EN", flag: "https://flagcdn.com/w40/gb.png" },
    { code: "fr", label: "FR", flag: "https://flagcdn.com/w40/fr.png" },
    { code: "es", label: "ES", flag: "https://flagcdn.com/w40/es.png" },
    { code: "he", label: "HE", flag: "https://flagcdn.com/w40/il.png" },
  ]

  // Close menu when clicking outside or scrolling
  useEffect(() => {
    if (!isMenuOpen) return;
    
    const handleClickOutside = (event) => {
      // Check if click is outside both the nav and both menu toggle buttons
      if (
        navRef.current && 
        !navRef.current.contains(event.target) && 
        ((desktopMenuToggleRef.current && !desktopMenuToggleRef.current.contains(event.target)) || !desktopMenuToggleRef.current) &&
        ((mobileMenuToggleRef.current && !mobileMenuToggleRef.current.contains(event.target)) || !mobileMenuToggleRef.current)
      ) {
        setIsMenuOpen(false);
      }
    };
    
    const handleScroll = () => {
      setIsMenuOpen(false);
    };
    
    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('scroll', handleScroll);
    
    // Clean up event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMenuOpen]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    if (!isMobileLanguageDropdownOpen) return;
    
    const handleClickOutside = (event) => {
      if (mobileLanguageDropdownRef.current && !mobileLanguageDropdownRef.current.contains(event.target)) {
        setIsMobileLanguageDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileLanguageDropdownOpen]);

  // Close desktop language dropdown when clicking outside
  useEffect(() => {
    if (!isDesktopLanguageDropdownOpen) return;
    
    const handleClickOutside = (event) => {
      if (desktopLanguageDropdownRef.current && !desktopLanguageDropdownRef.current.contains(event.target)) {
        setIsDesktopLanguageDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDesktopLanguageDropdownOpen]);

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <div className="logo">
            <a href="/">
              <img src="https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569958/Logo_b6xvr2.png" alt="Gold House Logo" className="logo-image" />
            </a>
          </div>

          <nav ref={navRef} className={`nav ${isMenuOpen ? "active" : ""} ${language === 'he' ? 'rtl' : ''}`}>
            {language === 'he' ? (
              <>
                <a href="/#contact">{t("contactTitle")}</a>
                <a href="/#about">{t("aboutTitle")}</a>
                <a href="/blog">{t("blogTitle")}</a>
                <a href="/#apartments">{t("apartments")}</a>
                <a href="/">{t("home")}</a>
              </>
            ) : (
              <>
                <a href="/">{t("home")}</a>
                <a href="/#apartments">{t("apartments")}</a>
                <a href="/blog">{t("blog")}</a>
                <a href="/#about">{t("about")}</a>
                <a href="/#contact">{t("contact")}</a>
              </>
            )}
          </nav>

          <div className="header-right">
            <div className="language-selector" ref={desktopLanguageDropdownRef}>
              <button 
                className={`language-toggle ${isDesktopLanguageDropdownOpen ? 'active' : ''}`}
                onClick={() => setIsDesktopLanguageDropdownOpen(!isDesktopLanguageDropdownOpen)}
              >
                <img 
                  src={languages.find(lang => lang.code === language)?.flag} 
                  alt={languages.find(lang => lang.code === language)?.label} 
                  className="flag-icon" 
                />
                <span className="desktop-lang">{languages.find(lang => lang.code === language)?.label}</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="12" 
                  height="12" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className={`dropdown-arrow ${isDesktopLanguageDropdownOpen ? 'open' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              {isDesktopLanguageDropdownOpen && (
                <div className="language-dropdown-menu desktop">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`language-dropdown-item ${language === lang.code ? "active" : ""}`}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsDesktopLanguageDropdownOpen(false);
                      }}
                    >
                      <img src={lang.flag} alt={lang.label} className="flag-icon" />
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button ref={desktopMenuToggleRef} className={`menu-toggle ${isMenuOpen ? "active" : ""}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>

          {/* Mobile-only elements that will be positioned with CSS */}
          <div className="mobile-header-left">
            <div className="mobile-language-dropdown" ref={mobileLanguageDropdownRef}>
              <button 
                className="mobile-language-toggle"
                onClick={() => setIsMobileLanguageDropdownOpen(!isMobileLanguageDropdownOpen)}
              >
                <img 
                  src={languages.find(lang => lang.code === language)?.flag} 
                  alt={languages.find(lang => lang.code === language)?.label} 
                  className="flag-icon" 
                />
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="12" 
                  height="12" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className={`dropdown-arrow ${isMobileLanguageDropdownOpen ? 'open' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              {isMobileLanguageDropdownOpen && (
                <div className="language-dropdown-menu open">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`language-dropdown-item ${language === lang.code ? "active" : ""}`}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsMobileLanguageDropdownOpen(false);
                      }}
                    >
                      <img src={lang.flag} alt={lang.label} className="flag-icon" />
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="mobile-header-right">
            <button ref={mobileMenuToggleRef} className={`menu-toggle mobile-menu-toggle ${isMenuOpen ? "active" : ""}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header